import { useMemo, useState } from "react";
import type { PowerSpotSeriesPayload } from "../lib/api";
import { ChartCard } from "./ChartCard";
import { ChartLegend } from "./ChartLegend";
import { LineChart } from "./LineChart";
import { berlinIso, type Series } from "../lib/chart";

interface Props {
  data: PowerSpotSeriesPayload;
}

type ForecastVariant = "ec00" | "ec00PriceDrop";

const COLORS = {
  spot: "#38bdf8", // sky-400
  forecast: "#a78bfa", // violet-400
  base: "#34d399", // emerald-400
  peak: "#f59e0b", // amber-500
};

/**
 * 15-min EPEX Day-Ahead actuals + Montel-AI forecast on a single x-axis.
 * The forecast is dashed; a vertical "Jetzt" line separates past/future.
 * Base/Peak daily means are toggleable step-after overlays so the team can
 * sanity-check the AI forecast against the conventional aggregates.
 */
export function PowerSpotChart({ data }: Props) {
  const [variant, setVariant] = useState<ForecastVariant>("ec00");
  const [showBase, setShowBase] = useState(true);
  const [showPeak, setShowPeak] = useState(true);
  const [showActual, setShowActual] = useState(true);
  const [showForecast, setShowForecast] = useState(true);

  const series: Series[] = useMemo(() => {
    const out: Series[] = [];
    if (showActual) {
      out.push({
        id: "actual",
        label: "EPEX DE-LU 15-Min",
        color: COLORS.spot,
        points: data.quarters.map((p) => ({ x: p.ts, y: p.value })),
      });
    }
    if (showForecast) {
      const forecastPoints =
        variant === "ec00" ? data.forecastEc00 : data.forecastEc00PriceDrop;
      out.push({
        id: "forecast",
        label: variant === "ec00" ? "Montel AI EC00" : "Montel AI EC00+PriceDrop",
        color: COLORS.forecast,
        dashed: true,
        points: forecastPoints.map((p) => ({ x: p.ts, y: p.value })),
      });
    }
    if (showBase) {
      out.push({
        id: "base",
        label: "Base (00–24)",
        color: COLORS.base,
        step: true,
        // One discrete step per day with an explicit null gap between days.
        // Without the gap, step-after would draw a continuous staircase
        // hopping from one day's value to the next — visually suggesting a
        // single running mean instead of independent daily aggregates.
        points: data.daily.flatMap((d) => [
          { x: berlinIso(d.date, 0, 0), y: d.base },
          { x: berlinIso(d.date, 23, 59), y: d.base },
          { x: berlinIso(d.date, 23, 59), y: null },
        ]),
      });
    }
    if (showPeak) {
      out.push({
        id: "peak",
        label: "Peak (08–20)",
        color: COLORS.peak,
        step: true,
        // EPEX Peak window is h09–h20 (clock hours 08:00–20:00 CET/CEST,
        // Mon–Fri). Each day's segment is its own step + an explicit null
        // gap so the off-peak hours (20:00–08:00 next day) are visible as
        // breaks — not artificially bridged.
        points: data.daily.flatMap((d) => [
          { x: berlinIso(d.date, 8, 0), y: d.peak },
          { x: berlinIso(d.date, 20, 0), y: d.peak },
          { x: berlinIso(d.date, 20, 0), y: null },
        ]),
      });
    }
    return out;
  }, [data, variant, showActual, showForecast, showBase, showPeak]);

  return (
    <ChartCard
      title="Spot 15-Min: Verlauf & Prognose"
      unit="EUR/MWh"
      accent="sky"
      updatedAt={data.updatedAt}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <ChartLegend
            entries={[
              {
                id: "actual",
                label: "EPEX 15-Min",
                color: COLORS.spot,
                visible: showActual,
              },
              {
                id: "forecast",
                label: variant === "ec00" ? "Prognose EC00" : "Prognose EC00+PD",
                color: COLORS.forecast,
                dashed: true,
                visible: showForecast,
              },
              {
                id: "base",
                label: "Base (00–24)",
                color: COLORS.base,
                visible: showBase,
              },
              {
                id: "peak",
                label: "Peak (08–20)",
                color: COLORS.peak,
                visible: showPeak,
              },
            ]}
            onToggle={(id) => {
              if (id === "actual") setShowActual((v) => !v);
              else if (id === "forecast") setShowForecast((v) => !v);
              else if (id === "base") setShowBase((v) => !v);
              else if (id === "peak") setShowPeak((v) => !v);
            }}
          />
          {/* Variant switch lives in the footer next to the legend so the
              card header stays clean and the two related controls (legend
              toggles + forecast variant) are visually grouped. */}
          <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 text-xs">
            <VariantBtn label="EC00" active={variant === "ec00"} onClick={() => setVariant("ec00")} />
            <VariantBtn
              label="EC00+PD"
              active={variant === "ec00PriceDrop"}
              onClick={() => setVariant("ec00PriceDrop")}
            />
          </div>
        </div>
      }
    >
      {hasAnyError(data) && (
        <div className="mb-2 text-[11px] text-rose-700 dark:text-rose-300">
          Teilweise unvollständige Daten:{" "}
          {Object.entries(data.errors)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")}
        </div>
      )}
      <LineChart series={series} unit="€/MWh" nowMarker={data.now} height={300} />
    </ChartCard>
  );
}

function VariantBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2.5 py-1 rounded-md font-medium transition " +
        (active
          ? "bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
          : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100")
      }
    >
      {label}
    </button>
  );
}

function hasAnyError(d: PowerSpotSeriesPayload): boolean {
  return Object.keys(d.errors).length > 0;
}
